import { clearAccessToken, setAccessToken } from '@podus/auth';
import type { SessionUser } from '@/types';

/**
 * Session storage for both roles on this platform.
 *
 * Ambassadors authenticate here directly and get a scoped JWT that only works
 * against the ambassador API. Admins arrive from auth.podus.org holding an
 * ordinary Podus token — that one is *also* mirrored into the shared
 * `p-ai-accessToken` cookie so moving between Podus products doesn't force a
 * second sign-in.
 */

const TOKEN_KEY = 'ab_token';
const USER_KEY = 'ab_user';

export function getToken(): string | null {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
}

export function getUser(): SessionUser | null {
    try {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? (JSON.parse(raw) as SessionUser) : null;
    } catch {
        return null;
    }
}

export function saveSession(token: string, user: SessionUser): void {
    try {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
        // Private-mode browsers can refuse localStorage; the in-memory context
        // still holds the session for this tab.
    }

    // Only a Podus-issued admin token is valid across other Podus products.
    // An ambassador token is scoped to this API surface and must not leak into
    // the shared cookie.
    if (user.role === 'admin') setAccessToken(token);
}

export function clearSession(): void {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    } catch {
        // ignore
    }
    clearAccessToken();
}
